// lib/cascade/hypotheses.js
// ═══════════════════════════════════════════════════════════
// HYPOTHESIS REGISTRY — H-Continuum-1 through H-18 + H-3v3
//
// Source: docs/CONTINUUM_HYPOTHESIS_FORMAT.md §IV (retrofitted),
// §V (Tier 1 forward), §V-bis (cascade integration + transfers).
//
// Read-only v1: hardcoded data, source-of-truth is the doc.
// Future (Phase 6/7): migrate to DB-backed with editable status.
//
// Status values: active | confirmed | rejected | respecified |
// retired | survived-respecified | proposed
// ═══════════════════════════════════════════════════════════

export const HYPOTHESES = [
  {
    id: 'H-Continuum-1',
    title: 'Country-aggregate wanting variables fail',
    group: 'retrofitted',
    status: 'confirmed',
    phaseAStatus: 'survived',
    created: '2026-04-30',
    claimSummary: 'National attitudinal/wellbeing aggregates (Cantril Ladder, WVS trust) do not predict TFR within high-income countries when income tier is controlled.',
    falsifier: 'Any single attitudinal variable showing p<0.0125 with effect size meaningfully larger than income-tier-alone baseline.',
    casePair: 'Japan vs Israel within high-income tier',
    pairLogic: 'MSSD',
    relatedHypotheses: ['H-Continuum-2v2'],
  },
  {
    id: 'H-Continuum-2',
    title: 'Civic architecture as direct cause (RETIRED)',
    group: 'retrofitted',
    status: 'retired',
    phaseAStatus: 'killed',
    created: '2026-04-30',
    killedDate: '2026-04-29',
    supersededBy: 'H-Continuum-2v2',
    claimSummary: 'Secular Israeli vs Korean TFR divergence directly caused by civic architecture. KILLED — Singapore Chinese has same bundle, Korea-like outcome.',
    falsifier: 'Killed by Singapore counter-example.',
    casePair: 'Israel vs Korea (original)',
    pairLogic: 'MSSD',
    relatedHypotheses: ['H-Continuum-2v2'],
  },
  {
    id: 'H-Continuum-2v2',
    title: 'Civic architecture as moderator of fiscal pronatalism',
    group: 'retrofitted',
    status: 'survived-respecified',
    phaseAStatus: 'survived-respecified',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): SURVIVED-RESPECIFIED. Estonia counter-case (3.29% spending + medium civic-arch + projected 2050 TFR 1.51) wounds the multiplicative-moderator claim. Survives in narrowed form: existential-threat civic architecture (Israel-specific) is moderator; general civic-architecture is not. Recommend folding narrowed claim into H-Continuum-6 framework. See docs/CONTINUUM_PHASE_A_BATCH_1.md.',
    created: '2026-04-30',
    claimSummary: 'Civic architecture (festival density × identity-preservation × coordination channels) is a multiplicative moderator that determines whether fiscal pronatalism produces TFR uplift.',
    falsifier: 'Any country with high civic-architecture, low pronatalist spending, TFR < 1.5 sustained for 10+ years.',
    casePair: 'Korea vs Israel + 5 cross-country with varying civic-arch × spend',
    pairLogic: 'MSSD interaction',
    relatedHypotheses: ['H-Continuum-2', 'H-Continuum-11'],
  },
  {
    id: 'H-Continuum-3',
    title: 'Four-country structural-feature convergence (RETIRED)',
    group: 'retrofitted',
    status: 'retired',
    phaseAStatus: 'killed',
    created: '2026-04-30',
    killedDate: '2026-04-29',
    supersededBy: 'H-Continuum-3v2',
    claimSummary: 'Pattern-breakers (Uruguay, Estonia, Latvia, Oman) maintain divergence due to shared structural features. KILLED — Taiwan ticks the features but is in trapped cluster.',
    falsifier: 'Killed by Taiwan counter-example.',
    casePair: '4 pattern-breakers vs 4 trapped',
    pairLogic: 'cross-case stratified',
    relatedHypotheses: ['H-Continuum-3v2'],
  },
  {
    id: 'H-Continuum-3v2',
    title: 'Late-demographic-transition timing as the actual mechanism',
    group: 'retrofitted',
    status: 'survived',
    phaseAStatus: 'survived',
    created: '2026-04-30',
    phaseARunDate: '2026-05-05',
    claimSummary: 'Pattern-breaker status reflects later transition stage; by 2050 they converge to trapped cluster.',
    falsifier: 'Any pattern-breaker projected 2050 TFR > trapped median by 0.5+ wounds the claim.',
    casePair: '4 pattern-breakers vs 4 trapped (projected to 2050)',
    pairLogic: 'MSSD with predicted convergence',
    relatedHypotheses: ['H-Continuum-3', 'H-Continuum-3v3 (retired)'],
    phaseAVerdict: 'Two Phase A runs preserved as discrete events. (1) 2022 data, original run: gap 0.54 → SURVIVED-RESPECIFIED → H-3v3 proposed. (2) 2024 data, re-run on live UN WPP: gap 0.45 → SURVIVED. Late-transition-timing-as-dominant gains support; structural features become case-specific. H-3v3 retires; H-3v2 stands without respecification. Live verdict: SURVIVED.',
  },
  {
    id: 'H-Continuum-3v3',
    title: 'Conjoint structural features predict pattern-breaker status',
    group: 'cascade-integration',
    status: 'retired',
    phaseAStatus: 'not-yet-run',
    created: '2026-05-05',
    killedDate: '2026-05-05',
    supersededBy: 'H-Continuum-3v2 (which survives on 2024 data without respec)',
    claimSummary: 'Pattern-breaker differential maintains because of conjoint structural features — metapopulation refugia + narrative-architecture coherence + (secondarily) later transition stage. RETIRED: respec motivation (2022-data gap > 0.5) not supported by 2024-revision data (gap 0.45, late-transition survives).',
    falsifier: 'Retired before formal Phase A test. The motivating gap shifted away from the falsifier zone in 2024 data.',
    casePair: '4 pattern-breakers + 4 trapped (matched on transition stage)',
    pairLogic: 'cross-case stratified',
    relatedHypotheses: ['H-Continuum-3v2', 'H-Continuum-13', 'H-Continuum-16', 'H-Continuum-2v2'],
  },
  {
    id: 'H-Continuum-4',
    title: 'LPI 22.4% recovery base rate (respecified)',
    group: 'retrofitted',
    status: 'confirmed',
    phaseAStatus: 'survived-respecified',
    created: '2026-04-30',
    claimSummary: 'Wildlife recovery from collapse follows ~22.4% base rate jointly determined by life-history × stressor reversibility × institutional fit. Mechanism is biological-physical, not political-legibility.',
    falsifier: 'Life-history × stressor-reversibility interaction term doesn\'t improve over base-rate-alone (ΔAUC < 0.05).',
    casePair: 'Atlantic cod vs bald eagle',
    pairLogic: 'MSSD on intervention strength',
    relatedHypotheses: ['H-Continuum-13', 'H-Continuum-14'],
  },
  {
    id: 'H-Continuum-5',
    title: 'Iran TFR reversal mechanism',
    group: 'tier-1-forward',
    status: 'survived',
    phaseAStatus: 'survived',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): SURVIVED. Joint-deployment claim is empirically supported. Iran is the canonical case where managed early-transition acceleration was deliberately produced. Within-case longitudinal evidence is clean: TFR inflection ~1986, accelerates post-1989 family-planning reversal, reaches ~1.7 by 2010. All four levers fired in coordinated fashion.',
    created: '2026-04-30',
    claimSummary: 'Iran 1985-2010 TFR collapse caused by joint deployment of female literacy + family planning + economic shock + urbanization, under early-transition conditions where these levers retain pull.',
    falsifier: 'TFR began declining significantly before 1986 or after 1991 in a way not aligned with lever timing.',
    casePair: 'Iran 1986-2011 (within-case longitudinal)',
    pairLogic: 'within-case',
    relatedHypotheses: ['H-Continuum-3v2'],
  },
  {
    id: 'H-Continuum-6',
    title: 'Korea-Israel constellation divergence',
    group: 'tier-1-forward',
    status: 'survived-respecified',
    phaseAStatus: 'survived-respecified',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): SURVIVED-RESPECIFIED. Israel\'s 4× TFR is heavily driven by ultra-orthodox sub-population (TFR ~6.5); strip ultra-orthodox and secular-Jewish TFR ~1.7 — still above Korea but not 4×. Iran (high-narrative) approaches falsifier zone. Survives in narrowed form: existential-narrative architecture preserves TFR in sub-populations that internalize it (refugia framing), not in population at large. Respec routes through H-Continuum-16.',
    created: '2026-04-30',
    claimSummary: '4× TFR divergence between Korea (~0.78) and Israel (~3.0) explained by national-survival-narrative density that modulates whether pronatalist disposition converts to actual fertility.',
    falsifier: 'A country with high national-survival-narrative density and TFR < 1.5 sustained 10+ years.',
    casePair: 'Korea 2000-2025 vs Israel 2000-2025',
    pairLogic: 'MSSD',
    relatedHypotheses: ['H-Continuum-2v2', 'H-Continuum-7'],
  },
  {
    id: 'H-Continuum-7',
    title: 'Korea-Italy structural invariant (RETIRED)',
    group: 'tier-1-forward',
    status: 'retired',
    phaseAStatus: 'killed',
    created: '2026-04-30',
    killedDate: '2026-05-06',
    supersededBy: 'H-Continuum-21 (proto, metapopulation structure) + H-Continuum-13 (eco refugia transfer)',
    claimSummary: 'KILLED 2026-05-06. UN WPP 2024 shows Korea 2050 TFR 1.03 vs Italy 1.35 — gap 0.32, NOT convergence. Mahalanobis distance on metapopulation structure alone is well above 1σ (Korea panmictic vs Italy structured). Korea overtakes Italy on the way down rather than converging with it. The MDSD comparison still produces useful findings but those go to H-21 (metapopulation, proto) and H-13 (eco refugia transfer), not to a structural-invariant claim.',
    falsifier: 'Killed by 2024 UN WPP data showing 0.32-gap divergence, not convergence.',
    casePair: 'Korea 2000-2025 vs Italy 2000-2025',
    pairLogic: 'MDSD',
    relatedHypotheses: ['H-Continuum-3v2', 'H-Continuum-13', 'H-Continuum-21 (proto)'],
  },
  {
    id: 'H-Continuum-8',
    title: 'Quebec Quiet Revolution cultural-transmission shift',
    group: 'tier-1-forward',
    status: 'survived',
    phaseAStatus: 'survived',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): SURVIVED. Within-society DiD with economic controls supports cultural-transmission as Quebec-specific dominant mechanism. Catholic mass attendance 90% → 25% in same window as TFR collapse 3.8 → 1.8; cégep/university expansion 1965-1985 dramatic. General cultural-shift confound exists but doesn\'t fully absorb. Anchors H-Continuum-2v2\'s narrowed civic-architecture claim — Catholic civic-architecture mattered when present; its removal moved TFR.',
    created: '2026-04-30',
    claimSummary: 'Quebec\'s 1965-1985 TFR collapse (~3.8 → ~1.8) caused by cultural-transmission shift (Quiet Revolution: secularization, education reform, decline of Catholic authority over family).',
    falsifier: 'Economic controls fully absorb the Quebec-vs-RoC TFR gap (DiD coefficient on Quebec dummy insignificant).',
    casePair: 'Quebec 1955-1995 vs rest-of-Canada',
    pairLogic: 'within-society',
    relatedHypotheses: ['H-Continuum-2v2'],
  },
  {
    id: 'H-Continuum-9',
    title: 'Population decline as 9-spiral coupled cascade',
    group: 'cascade-integration',
    status: 'active',
    phaseAStatus: 'pending-formal-test',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): INCONCLUSIVE — qualitative coupling supports ~4-5 clusters in Korea + Japan, but ≥6 quantitative threshold requires formal VAR / Granger-causality testing on collected time-series. Cannot verdict cleanly without Phase 7 step 5+ data work. Marked pending-formal-test pending data infrastructure.',
    created: '2026-05-04',
    claimSummary: '≥6 of 9 spirals exhibit measurable cross-spiral feedback within 1-5 year lag in late-transition advanced economies. The "9 independent challenges" null is rejected.',
    falsifier: '<4 of 9 spirals show ANY significant cross-spiral Granger-causal link in either country after FDR.',
    casePair: 'Korea 2000-2025 vs Japan 1995-2020',
    pairLogic: 'MSSD',
    relatedHypotheses: ['H-Continuum-10', 'H-Continuum-15'],
  },
  {
    id: 'H-Continuum-10',
    title: 'Cascade arrest requires ≥3 of 4 intervention layers',
    group: 'cascade-integration',
    status: 'active',
    phaseAStatus: 'not-yet-run',
    created: '2026-05-04',
    claimSummary: 'Cascades arrest if and only if ≥3 of 4 layers (Personal, Corporate, Policy, Cultural) show measurable activation. ≤2 layers = continued decline. Layer count, not intensity, dominates.',
    falsifier: '≥2 cases with all 4 layers engaged AND continued decline. OR ≥2 cases with ≤1 layer engaged AND TFR arrest.',
    casePair: '7 advanced economies (Korea, Japan, Italy, Spain, Singapore, Hungary, France)',
    pairLogic: 'cross-case stratified',
    relatedHypotheses: ['H-Continuum-9', 'H-Continuum-11', 'H-Continuum-12'],
  },
  {
    id: 'H-Continuum-11',
    title: 'Cultural-layer is necessary but not sufficient',
    group: 'cascade-integration',
    status: 'active',
    phaseAStatus: 'not-yet-run',
    created: '2026-05-04',
    claimSummary: 'No advanced economy arrests TFR cascade with ≥3 layers engaged but Cultural absent. Cultural alone (without ≥2 supporting layers) is insufficient. Pinpoints why ₩500T failed.',
    falsifier: '≥1 advanced economy with sustained arrest WITHOUT measurable Cultural-layer engagement.',
    casePair: 'Hungary 2010-2024 (critical case) within H-10 dataset',
    pairLogic: 'critical-case design',
    relatedHypotheses: ['H-Continuum-10', 'H-Continuum-2v2'],
  },
  {
    id: 'H-Continuum-12',
    title: 'Eco arrest requires human reflexive layer ≥3 sublayers',
    group: 'cascade-integration',
    status: 'active',
    phaseAStatus: 'not-yet-run',
    created: '2026-05-04',
    claimSummary: 'Substrate-symmetric counterpart of H-10. Eco cascade arrest requires ≥3 of 4 sublayers (Individual, Industry, Policy, Cultural) of human-reflexive-layer engagement.',
    falsifier: '≥2 eco arrests with ≤1 sublayer engaged. OR ≥2 cascades with all 4 sublayers engaged but continued decline.',
    casePair: '8 well-documented eco cases (bald eagle, cod, wolf, gray whale, sea otter, Florida panther, American chestnut, California condor)',
    pairLogic: 'cross-case stratified',
    relatedHypotheses: ['H-Continuum-10', 'H-Continuum-15'],
  },
  {
    id: 'H-Continuum-13',
    title: 'Refugia + connectivity predict eco recovery vs. stabilization',
    group: 'cascade-integration',
    status: 'survived-respecified',
    phaseAStatus: 'survived-respecified',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): SURVIVED-RESPECIFIED. Case-pattern strongly supports refugia + connectivity vs. total-size for predicting recovery. Atlantic cod is the load-bearing case: Iceland source intact but limited connectivity to NW Atlantic → NW Atlantic remains stabilization-at-lower despite source presence. Survives in narrowed form: refugia + connectivity *to the affected sub-population* matters; pure refugia presence elsewhere doesn\'t transfer recovery.',
    created: '2026-05-04',
    claimSummary: 'Refugial-population presence + connectivity predicts recovery vs. stabilization-at-lower with greater accuracy (ΔAUC > 0.10) than total post-cascade size.',
    falsifier: 'Total size predicts outcome with AUC ≥ refugia/connectivity model AUC after controls.',
    casePair: 'LPI populations meeting cascade-and-outcome criteria',
    pairLogic: 'cross-case stratified',
    relatedHypotheses: ['H-Continuum-4', 'H-Continuum-15', 'H-Continuum-16'],
  },
  {
    id: 'H-Continuum-14',
    title: 'Hysteresis is the modal eco cascade outcome',
    group: 'cascade-integration',
    status: 'survived',
    phaseAStatus: 'survived',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): SURVIVED. H-Continuum-4 already produced 22.4% recovery base rate in LPI dataset → 77.6% NOT-recovered, well above the 60% non-recovery threshold. Eco hysteresis is empirically modal. Alternative-stable-states theory + conservation literature both support. Direct cross-substrate parallel for H-Continuum-18 (cultural-regime hysteresis).',
    created: '2026-05-04',
    claimSummary: '≥60% of post-cascade ecosystems stabilize at lower equilibrium rather than recover to baseline. Hysteresis is the rule.',
    falsifier: 'Recovery-to-baseline fraction ≥ stabilization-lower fraction in LPI+IUCN dataset.',
    casePair: 'All LPI populations meeting criteria + IUCN-tracked recovery cases',
    pairLogic: 'cross-case stratified',
    relatedHypotheses: ['H-Continuum-4', 'H-Continuum-13'],
  },
  {
    id: 'H-Continuum-15',
    title: 'Cross-substrate mechanism transfer (KEYSTONE)',
    group: 'cascade-integration',
    status: 'active',
    phaseAStatus: 'not-yet-run',
    keystone: true,
    created: '2026-05-04',
    claimSummary: 'Allee, hysteresis, phenological mismatch / velocity-incoherence operate in both eco and human cascades with measurable structural parallelism. Empirical test of V1→V2 unified-instrument thesis.',
    falsifier: 'Zero of 3 mechanism categories shows significant cross-substrate association at p<0.05 (Bonferroni).',
    casePair: '5 eco + 5 human paired cases (cod↔Korea, eagle↔Israel, wolf↔Iran, otter↔Italy, panther↔Japan)',
    pairLogic: 'cross-substrate analog matching',
    relatedHypotheses: ['H-Continuum-9', 'H-Continuum-10', 'H-Continuum-11', 'H-Continuum-12', 'H-Continuum-13', 'H-Continuum-14', 'H-Continuum-17', 'H-Continuum-18'],
  },
  {
    id: 'H-Continuum-16',
    title: 'Demographic refugia transfer (eco-to-human)',
    group: 'cross-substrate-transfer',
    status: 'survived',
    phaseAStatus: 'survived',
    phaseARunDate: '2026-05-06',
    phaseAVerdict: 'Phase A Batch 1 (2026-05-06): SURVIVED on the existence claim. Refugia (sub-population TFR ≥1.8) demonstrably present in Hungary (Roma) within case set + US Mormon comparison case (Mormon Utah ~3.0, ultra-orthodox ~6.5, Amish ~7.0). Falsifier required ZERO; we have at least 1 in case set + comparisons. Israel TFR 2.31 by 2050 (UN WPP 2024) is the strongest single empirical anchor for refugia logic in the dataset. Second claim (correlation aspect, mechanism: selection vs density) needs longitudinal cohort data not yet pulled — flagged for Phase 7 follow-up.',
    created: '2026-05-05',
    claimSummary: 'Subcultural sub-populations with cooperative-breeding-density >2σ above national mean exhibit cohort TFR ≥1.8 and operate as recovery seeds (eco metapopulation parallel) rather than outliers.',
    falsifier: 'Zero of 5 declining advanced economies show measurable demographic refugia (sub-pop cohort TFR ≥1.8).',
    casePair: '5 declining economies + US Mormon comparison',
    pairLogic: 'cross-case stratified',
    relatedHypotheses: ['H-Continuum-13', 'H-Continuum-15'],
  },
  {
    id: 'H-Continuum-17',
    title: 'Allee threshold mechanism transfer (H-15 sub-test)',
    group: 'cross-substrate-transfer',
    status: 'active',
    phaseAStatus: 'not-yet-run',
    created: '2026-05-05',
    claimSummary: 'Korean TFR <1.0 reflects Allee-effect-like cooperative-breeding-density threshold breach. Threshold-non-linear, not continuous gradient. Korean sub-regions above threshold exhibit TFR ≥1.5; below show TFR <1.0.',
    falsifier: 'No threshold-like non-linearity — continuous model fits as well as threshold model under BIC.',
    casePair: 'Korean sub-regions 2010-2024 + Japan + Italy + cod Allee threshold',
    pairLogic: 'cross-country replication + cross-substrate analog',
    relatedHypotheses: ['H-Continuum-15', 'H-Continuum-9', 'H-Continuum-11'],
    criticalTBD: 'Korean sub-regional cooperative-breeding-density composite proxy requires non-trivial data construction (multi-gen household % + community childcare access + peer-parent density).',
  },
  {
    id: 'H-Continuum-18',
    title: 'Cultural-regime hysteresis transfer (H-15 sub-test)',
    group: 'cross-substrate-transfer',
    status: 'active',
    phaseAStatus: 'not-yet-run',
    created: '2026-05-05',
    claimSummary: 'Cultural regime shifts in family/fertility domain exhibit hysteresis. Pronatalist-spending elasticity post-shift ≤1/3 of pre-shift elasticity (≥3× different).',
    falsifier: 'Post-shift elasticity within 50% of pre-shift in ≥2 of 3 shifted cases. OR Israel control shows comparable discontinuity.',
    casePair: 'Korea (pre/post 2018) + France (pre/post 1970s) + Hungary (partial-shift) + Israel (control)',
    pairLogic: 'within-case longitudinal × cross-case stratified',
    relatedHypotheses: ['H-Continuum-15', 'H-Continuum-11', 'H-Continuum-14'],
  },
];

// Group → label
export const GROUP_LABEL = {
  'retrofitted': 'Retrofitted from pilots',
  'tier-1-forward': 'Tier 1 forward-registered',
  'cascade-integration': 'Cascade integration (H-9 to H-15)',
  'cross-substrate-transfer': 'Cross-substrate transfers (H-16 to H-18)',
};

// Status → color (Continuum tokens.js compatible)
export const STATUS_COLOR = {
  active: 'sage',
  confirmed: 'gold',
  proposed: 'amber',
  retired: 'dim',
  killed: 'terra',
  rejected: 'terra',
  respecified: 'purple',
  'survived-respecified': 'purple',
};

// Phase A status → label
export const PHASE_A_LABEL = {
  'not-yet-run': 'Not yet run',
  'survived': 'Survived',
  'killed': 'Killed',
  'survived-respecified': 'Survived-Respec',
};

// Compute counts (consumed by header / status strip)
export function getCounts() {
  const counts = {
    total: HYPOTHESES.length,
    active: HYPOTHESES.filter(h => h.status === 'active').length,
    confirmed: HYPOTHESES.filter(h => h.status === 'confirmed').length,
    proposed: HYPOTHESES.filter(h => h.status === 'proposed').length,
    retired: HYPOTHESES.filter(h => h.status === 'retired').length,
    'survived-respecified': HYPOTHESES.filter(h => h.status === 'survived-respecified').length,
  };
  return counts;
}
